import { NotificationProvider, OpenNotificationParams, useTranslation } from "@refinedev/core";
import { ProgressHTMLAttributes, useEffect, useReducer, useState, useRef } from "react";

interface ProgressProps extends ProgressHTMLAttributes<HTMLProgressElement> {
    timeout: number,
}

function Progress({ timeout, ...rest }: ProgressProps) {
    const [progress, setProgress] = useState(0)
    useEffect(() => {
        const a = setInterval(() => {
            setProgress(p => p + timeout / 1000)
        }, 100)

        return () => {
            clearInterval(a)
        }
    }, [])

    const { className, ...r } = rest
    return <progress className={`progress progress-secondary w-full h-1 ${className}`} max="1" value={progress}  {...r}></progress>
}

export function useNotificationProvider(ref: React.Ref<any>) {
    const [stack, dispatch] = useReducer((oldState: Record<string, OpenNotificationParams>, action: OpenNotificationParams | string) => {
        if (typeof action == 'string') {
            const { [action]: remove, ...rest } = oldState;
            return rest
        } else if (action) {
            return { ...oldState, [action.key ?? '']: action }
        }
        return oldState
    }, {} as Record<string, OpenNotificationParams>)

    const Notification = ({ open, notificationParams }: { open: boolean, notificationParams: OpenNotificationParams }) => {
        const { message, key, type, description, undoableTimeout } = notificationParams
        const [_open, setOpen] = useState(open)
        const { translate } = useTranslation()
        const timeoutRef = useRef<NodeJS.Timeout>()
        const remainingTimeRef = useRef<number>(undoableTimeout ? undoableTimeout * 1000 : 5000)
        const startTimeRef = useRef<number>(Date.now())

        const startTimer = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
            timeoutRef.current = setTimeout(() => {
                setOpen(false)
                if (key) {
                    setTimeout(() => dispatch(key), 500)
                }
            }, remainingTimeRef.current)
        }

        const pauseTimer = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                remainingTimeRef.current -= (Date.now() - startTimeRef.current)
            }
        }

        const resumeTimer = () => {
            startTimeRef.current = Date.now()
            startTimer()
        }

        useEffect(() => {
            if (open) {
                startTimeRef.current = Date.now()
                startTimer()
            }
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                }
            }
        }, [open])

        if (type == 'progress') {
            return <Progress className="fixed top-0 left-0 w-full z-50" timeout={undoableTimeout ?? 0} />
        }

        const close = () => {
            setOpen(false)
            setTimeout(() => dispatch(key ?? ''), 500)
        }

        const [desc, ...rest] = (description || '').split(':')

        return (
            <div 
                className={`
                    fixed bottom-4 right-4 z-50 
                    transform transition-all duration-300 ease-in-out
                    ${_open ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
                    w-full sm:w-auto sm:max-w-lg
                `}
                onMouseEnter={pauseTimer}
                onMouseLeave={resumeTimer}
            >
                <div className="alert shadow-lg bg-black/90 text-base-100 rounded-lg">
                    <div className="flex-1">
                        <div className="collapse">
                            <input type="checkbox" className="peer" />
                            <div className="collapse-title text-sm font-medium">
                                {translate(message)}
                                {desc ? ` : ${translate(desc)}` : ''}
                            </div>
                            {rest.length > 0 && (
                                <div className="collapse-content text-sm opacity-80">
                                    {rest.join(':')}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex-none">
                        <button
                            className="btn btn-ghost btn-sm text-base-100 hover:text-primary"
                            onClick={close}
                        >
                            {translate("app.close")}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return [
        {
            open: (msg) => {
                dispatch(msg)
            },
            close: (key) => {
                dispatch(key)
            },
        },
        () => {
            return (
                <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
                    {Object.values(stack).map((e, i) => (
                        <Notification key={i} open={true} notificationParams={e} />
                    ))}
                </div>
            )
        }
    ] as [NotificationProvider, any]
}
